// ── Provider-admin sub-account scope (shared, cycle-free) ───────────────────
// A normal provider is locked to their own key server-side and sends NO
// provider param. A provider_admin (Dr. Basmaa), however, ACTS AS a chosen
// provider within her practice — the dropdown in the provider layout sets the
// selected key here, and the axios request interceptor (see client.ts) threads
// it as `?provider=` on EVERY provider-scoped request. The backend validates
// the selection against the providers that exist in HER practice
// (out-of-practice keys → 403), so this is scope SELECTION, never widening.
// When null (a normal provider, or "all" not chosen) no param is sent and the
// server falls back to the caller's own scope.
//
// This lives in its own module (NOT provider.ts) so client.ts can read the
// selection from its request interceptor without importing provider.ts, which
// would create a circular import (provider.ts → client.ts).

let _selectedProviderKey: string | null = null;

/** Set the provider_admin's active sub-account (null clears → own/all scope). */
export function setSelectedProviderKey(key: string | null): void {
  _selectedProviderKey = key && key.trim() ? key.trim() : null;
}

/** The provider_admin's currently-selected sub-account key (or null). */
export function getSelectedProviderKey(): string | null {
  return _selectedProviderKey;
}

/** Request paths that are provider-scoped and must carry the ?provider= param. */
export function isProviderScopedPath(url: string | undefined): boolean {
  if (!url) return false;
  // `/provider-report/operating-health` is the ONE endpoint under that prefix
  // that is practice-wide by definition. It ignores the param server-side
  // (its gate takes no selector), but sending it would imply the practice
  // report narrows to one doctor, which it must never do.
  if (url.includes("/provider-report/operating-health")) return false;
  // Dedicated provider endpoints + the provider-gated bakeoff writes the
  // doctor-web app reuses (dx-match / write-single / queue / void-line / status).
  //
  // `/provider-report/` is the My Revenue surface. It MUST be listed here: a
  // provider_admin acting as one doctor otherwise sends no selector, and the
  // backend correctly falls back to whole-practice scope — which rendered every
  // provider's cash and AR combined ($11.41M lifetime, 5,597 stuck claims)
  // under a single doctor's name. A normal provider is unaffected either way,
  // since their key is forced server-side from the JWT.
  return (
    url.includes("/revenue/provider/") ||
    url.includes("/revenue/bakeoff/") ||
    url.includes("/provider-report/")
  );
}
